import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getAuthConfig } from '../utils/auth';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Check, Shield, Truck, Tag, Clock, Package, BarChart3, Bell, Star } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function Membership() {
  const navigate = useNavigate();
  const [membership, setMembership] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    const fetchMembership = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/membership/tiers`);
        // Get the single membership tier
        if (response.data.tiers && response.data.tiers.length > 0) {
          setMembership(response.data.tiers[0]);
        }
      } catch (error) {
        console.error('Error fetching membership:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMembership();
  }, []);

  const handleJoin = async () => {
    setProcessing(true);
    try {
      // Check if user is logged in
      await axios.get(`${BACKEND_URL}/api/auth/me`, getAuthConfig());
      
      // Upgrade to member tier
      await axios.post(
        `${BACKEND_URL}/api/membership/upgrade`,
        { tier: 'member' },
        getAuthConfig()
      );
      
      toast.success('Welcome to BlueCrate! Your membership is now active.');
      navigate('/dashboard');
    } catch (error) {
      if (error.response?.status === 401) {
        // Not logged in - redirect to auth
        const redirectUrl = window.location.origin + '/membership';
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      } else {
        toast.error('Failed to process membership. Please try again.');
      }
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
        <Navbar />
        <div className="pt-32 flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-500">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  const featureIcons = [Tag, Star, Shield, Tag, BarChart3, Bell];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <Navbar />
      
      <div className="pt-32 pb-20 px-4 sm:px-6 lg:px-8" data-testid="membership-page">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-6">
              <Star className="w-4 h-4 text-primary" />
              <span className="text-sm font-semibold text-primary">Members Save Up To 30%</span>
            </div>
            <h1 className="font-chivo font-black text-4xl md:text-6xl tracking-tight leading-none mb-4" data-testid="membership-title">
              BlueCrate Membership
            </h1>
            <p className="font-manrope text-base md:text-lg leading-relaxed text-gray-600 max-w-2xl mx-auto">
              Join thousands of contractors and homeowners who save big on premium flooring materials through our wholesale co-op model.
            </p>
          </div>

          {/* Main Membership Card */}
          {membership && (
            <div className="max-w-2xl mx-auto mb-16">
              <div className="relative bg-white rounded-3xl shadow-2xl border-2 border-primary/20 overflow-hidden">
                {/* Recommended Badge */}
                <div className="absolute top-0 right-0">
                  <div className="bg-primary text-white px-6 py-2 text-sm font-bold rounded-bl-2xl">
                    BEST VALUE
                  </div>
                </div>

                <div className="p-8 md:p-12">
                  {/* Price Section */}
                  <div className="text-center mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">{membership.name}</h2>
                    <div className="flex items-baseline justify-center gap-2">
                      <span className="text-6xl font-black text-primary">${membership.price}</span>
                      <div className="text-left">
                        <span className="text-gray-500 text-lg">/month</span>
                        <p className="text-sm text-gray-400">billed annually</p>
                      </div>
                    </div>
                    <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 rounded-full">
                      <Check className="w-4 h-4" />
                      <span className="font-semibold">${membership.annual_total}/year</span>
                    </div>
                  </div>

                  {/* Savings Example */}
                  {membership.savings_example && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-8 text-center">
                      <p className="text-amber-800 font-semibold">{membership.savings_example}</p>
                    </div>
                  )}

                  {/* Features */}
                  <div className="grid gap-4 mb-8">
                    {membership.features.map((feature, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          {featureIcons[index % featureIcons.length] && 
                            (() => {
                              const IconComponent = featureIcons[index % featureIcons.length];
                              return <IconComponent className="w-4 h-4 text-primary" />;
                            })()
                          }
                        </div>
                        <span className="text-gray-700 pt-1">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* CTA Button */}
                  <Button
                    onClick={handleJoin}
                    disabled={processing}
                    className="w-full h-14 text-lg font-bold bg-primary hover:bg-primary/90 rounded-full"
                    data-testid="join-membership-btn"
                  >
                    {processing ? (
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Processing...
                      </div>
                    ) : (
                      <>
                        <Star className="w-5 h-5 mr-2" />
                        Become a Member - ${membership.annual_total}/year
                      </>
                    )}
                  </Button>

                  <p className="text-center text-sm text-gray-500 mt-4">
                    Cancel anytime. Prorated refund available.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Comparison Section */}
          <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 mb-16">
            <h2 className="text-2xl font-bold text-center mb-8">Member vs Non-Member Pricing</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-4 px-4 font-semibold text-gray-600">Product</th>
                    <th className="text-center py-4 px-4 font-semibold text-gray-600">Non-Member</th>
                    <th className="text-center py-4 px-4 font-semibold text-primary bg-primary/5 rounded-t-lg">Member Price</th>
                    <th className="text-center py-4 px-4 font-semibold text-green-600">You Save</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-100">
                    <td className="py-4 px-4 text-gray-800 font-medium">Engineered Hardwood (per sqft)</td>
                    <td className="text-center py-4 px-4 text-gray-600">$3.56</td>
                    <td className="text-center py-4 px-4 font-bold text-primary bg-primary/5">$2.49</td>
                    <td className="text-center py-4 px-4 font-bold text-green-600">30%</td>
                  </tr>
                  <tr>
                    <td className="py-4 px-4 text-gray-800 font-medium">Luxury Vinyl Plank (per sqft)</td>
                    <td className="text-center py-4 px-4 text-gray-600">$2.24</td>
                    <td className="text-center py-4 px-4 font-bold text-primary bg-primary/5 rounded-b-lg">$1.57</td>
                    <td className="text-center py-4 px-4 font-bold text-green-600">30%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-center text-sm text-gray-500 mt-6">
              * Prices shown are base prices. Actual pricing may vary by color and style selection.
            </p>
          </div>

          {/* How It Works */}
          <div className="bg-gray-50 rounded-3xl p-8 md:p-12">
            <h2 className="font-chivo font-bold text-2xl mb-8 text-center">How BlueCrate Works</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="font-chivo font-bold text-2xl text-primary">01</span>
                </div>
                <h3 className="font-chivo font-semibold text-lg mb-2">Pledge Your Sq Ft</h3>
                <p className="text-sm text-gray-600">
                  Start with a minimum of 1,500 sqft. Pay 30% deposit to secure your spot.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="font-chivo font-bold text-2xl text-primary">02</span>
                </div>
                <h3 className="font-chivo font-semibold text-lg mb-2">Container Fills</h3>
                <p className="text-sm text-gray-600">
                  Your order joins others in a shared container. Production starts at 80% capacity.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="font-chivo font-bold text-2xl text-primary">03</span>
                </div>
                <h3 className="font-chivo font-semibold text-lg mb-2">Manufacturing</h3>
                <p className="text-sm text-gray-600">
                  Premium materials manufactured overseas. Ocean freight to US ports.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="font-chivo font-bold text-2xl text-primary">04</span>
                </div>
                <h3 className="font-chivo font-semibold text-lg mb-2">Delivered to You</h3>
                <p className="text-sm text-gray-600">
                  Pay your 70% balance and receive delivery to your door. 45-60 days total.
                </p>
              </div>
            </div>
          </div>

          {/* FAQ */}
          <div className="mt-16 text-center">
            <h2 className="font-bold text-xl mb-4">Have Questions?</h2>
            <p className="text-gray-600 mb-6">Check out our FAQ or contact our support team.</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button
                variant="outline"
                onClick={() => navigate('/faq')}
                className="rounded-full"
              >
                View FAQ
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/contact')}
                className="rounded-full"
              >
                Contact Us
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Membership;
